import { useConfirmation } from "./ConfirmationDialogContext";
import { useMutation } from "react-query";

class CancelError extends Error {
  constructor() {
    super("Mutation cancelled by user");
    this.name = "CancelError";
  }
}

/**
 * A wrapper around useMutation that asks for confirmation before executing the mutation.
 *
 * @param {function} mutationFn - The actual mutation function (e.g. API call).
 * @param {object} options - Same as useMutation options, with extra onCancel().
 * @param {object} confirmOptions - Title, message, confirmText, cancelText for the dialog.
 */
export const useConfirmedMutation = (mutationFn, options = {}, confirmOptions = {}) => {
  const { confirm } = useConfirmation();

  // extract onMutate and remove it from options, it will be run manually after confirmation
  const onMutate = options.onMutate;
  delete options.onMutate;

  return useMutation({
    ...options,
    mutationFn: async (variables) => {
      const userConfirmed = await confirm(confirmOptions);
      if (!userConfirmed) throw new CancelError();

      // Call onMutate manually here to ensure it happens *after* confirmation
      onMutate?.(variables);

      return mutationFn(variables);
    },

    // Override onError to handle CancelError gracefully
    onError: (error, variables, context) => {
      if (error instanceof CancelError) {
        // Invoke onCancel if the mutation was cancelled
        options.onCancel?.();
        return;
      }
      options.onError?.(error, variables, context);
    },

    // Pass through standard lifecycle hooks
    onSuccess: options.onSuccess,
    onSettled: options.onSettled,
  });
};
